import { useEffect, useState } from 'react';
import { api } from '../api/endpoints';
import type { PurchaseHistory, PurchaseLine } from '../api/types';
import { useDataVersion } from '../hooks/useDataVersion';
import { formatDateTime, formatMinutes, formatMoney, POOL_LABEL } from '../lib/format';
import { Badge, Button, EmptyState, ErrorNote, Spinner } from './ui';

const PAGE_SIZE = 25;

function KindBadge({ line }: { line: PurchaseLine }) {
  if (line.kind === 'recharge') return <Badge tone="sky">Recharge</Badge>;
  if (line.kind === 'ps5') return <Badge tone="sky">PS5</Badge>;
  if (line.kind === 'balance') return <Badge tone="amber">Balance paid</Badge>;
  return null;
}

// Everything this customer has paid for, newest first. Settled lines leave
// the tab, so without this a paid bill simply disappeared from the card —
// and "what did I have last time?" or a disputed bill had no answer. Paged
// with "Show more" because a regular's history runs to hundreds of lines.
export function PurchaseHistorySection({
  customerId,
  refreshKey = 0,
}: {
  customerId: string;
  /** Bumped by the parent when a payment elsewhere on the page settles lines. */
  refreshKey?: number;
}) {
  const [data, setData] = useState<PurchaseHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState(PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  const version = useDataVersion();

  // Refetch everything shown so far on any write: a payment moves lines in
  // at the top, and keeping the old page would show a stale list.
  useEffect(() => {
    const controller = new AbortController();
    api.customers
      .purchases(customerId, { limit: shown, offset: 0 }, controller.signal)
      .then((result) => {
        setData(result);
        setError(null);
      })
      .catch((err) => {
        if ((err as Error).name !== 'AbortError') setError((err as Error).message);
      })
      .finally(() => setLoadingMore(false));
    return () => controller.abort();
  }, [customerId, shown, refreshKey, version]);

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
          Purchase history
        </h2>
        {data && data.total_count > 0 && (
          <p className="text-xs text-slate-500">
            {data.total_count} paid line{data.total_count === 1 ? '' : 's'} ·{' '}
            {formatMoney(data.total_spent)} in all
          </p>
        )}
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {data === null && !error ? (
        <Spinner label="Loading purchases…" />
      ) : data && data.items.length === 0 ? (
        <EmptyState title="Nothing paid for yet." />
      ) : (
        data && (
          <>
            <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
              {data.items.map((line) => (
                <li
                  key={line.tab_item_id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
                >
                  <div className="min-w-0">
                    <p className="text-slate-200">
                      <span className="mr-1.5 align-middle">
                        <KindBadge line={line} />
                      </span>
                      {line.item_name}
                      {line.qty > 1 && <span className="text-slate-500"> ×{line.qty}</span>}
                    </p>
                    <p className="text-xs text-slate-500">
                      {line.recharge &&
                        `${formatMinutes(line.recharge.minutes_purchased)} ${POOL_LABEL[line.recharge.pool]} · `}
                      {line.recharge?.status === 'on_hold' && 'on hold · '}
                      paid {formatDateTime(line.paid_at)}
                      {line.payment_mode && ` · ${line.payment_mode.toUpperCase()}`}
                      {line.received_by_name && ` · ${line.received_by_name}`}
                    </p>
                  </div>
                  <span className="font-semibold text-slate-100">
                    {formatMoney(line.line_total)}
                  </span>
                </li>
              ))}
            </ul>
            {data.items.length < data.total_count && (
              <Button
                variant="secondary"
                className="w-full"
                disabled={loadingMore}
                onClick={() => {
                  setLoadingMore(true);
                  setShown((current) => current + PAGE_SIZE);
                }}
              >
                {loadingMore
                  ? 'Loading…'
                  : `Show more (${data.total_count - data.items.length} older)`}
              </Button>
            )}
          </>
        )
      )}
    </section>
  );
}
